import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * MultiSelectDropdown
 * --------------------------------------------------------------------------
 * Compact multi-select replacement for sprawling checkbox grids. Triggered
 * by a single button that shows "<N> selected"; clicking opens a popover
 * with a search input and a scrollable checkbox list. Selected values are
 * also shown as removable chips below the trigger so the user always sees
 * what's currently picked even when the popover is closed.
 *
 * Use anywhere a checkbox grid of options is becoming long enough to feel
 * cluttered (e.g., the Campaign Exclusion Search inside Add Lead).
 *
 * Props:
 *   options         Array of either strings OR `{ value, label, hint? }`
 *                   objects. When using object form, `value` is what gets
 *                   passed back via onChange; `label` is the display text;
 *                   `hint` is an optional smaller subtitle (e.g., email
 *                   beneath the user's display name).
 *   value           array of selected values
 *   onChange(next)  setter called with the new array of values
 *   placeholder     trigger placeholder when nothing is selected
 *   testid          base data-testid; sub-elements derive `-<suffix>` ids
 *   maxHeight       inner list scroll height (default 240px)
 *   showChips       render selected items as chips below trigger (default true)
 */
export function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  testid,
  maxHeight = "240px",
  showChips = true,
  label,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Normalize string options into {value, label, hint?} shape so the rest
  // of this component only deals with one structure. Pass-through for objs.
  const norm = useMemo(() => options.map((o) => (
    typeof o === "string" ? { value: o, label: o } : o
  )), [options]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return norm;
    return norm.filter((o) => {
      const blob = `${o.label || ""} ${o.hint || ""} ${o.value || ""}`.toLowerCase();
      return blob.includes(needle);
    });
  }, [norm, q]);

  const labelFor = (v) => {
    const m = norm.find((o) => o.value === v);
    return m?.label || v;
  };

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const selectAllVisible = () =>
    onChange(Array.from(new Set([...value, ...filtered.map((o) => o.value)])));
  const clearAll = () => onChange([]);

  const triggerLabel =
    value.length === 0 ? placeholder
    : value.length === 1 ? labelFor(value[0])
    : `${value.length} of ${norm.length} selected`;

  return (
    <div data-testid={testid}>
      {label && <div className="meta-label mb-1">{label}</div>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-trigger`}
            className="w-full inline-flex items-center justify-between gap-2 h-9 px-3 rounded-sm border border-input bg-background text-sm hover:bg-secondary/40"
          >
            <span className={`truncate ${value.length === 0 ? "text-muted-foreground" : ""}`}>
              {triggerLabel}
            </span>
            <ChevronDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[min(380px,90vw)] border-border rounded-sm"
          align="start"
          data-testid={`${testid}-popover`}
        >
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="h-8 rounded-sm pl-7 text-sm"
                data-testid={`${testid}-search`}
              />
            </div>
            <div className="flex items-center justify-between mt-1 px-0.5">
              <button
                type="button"
                onClick={selectAllVisible}
                className="text-[11px] text-primary hover:underline"
                data-testid={`${testid}-select-visible`}
                disabled={filtered.length === 0}
              >
                Select visible ({filtered.length})
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-[11px] text-muted-foreground hover:underline"
                data-testid={`${testid}-clear`}
                disabled={value.length === 0}
              >
                Clear all
              </button>
            </div>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No matches.
              </div>
            ) : (
              filtered.map((o) => {
                const checked = value.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-secondary/60 ${checked ? "bg-secondary/30" : ""}`}
                    data-testid={`${testid}-option-${o.value}`}
                  >
                    <Checkbox
                      className="rounded-sm h-3.5 w-3.5"
                      checked={checked}
                      onCheckedChange={() => toggle(o.value)}
                    />
                    <span className="truncate flex-1">
                      {o.label}
                      {o.hint && (
                        <span className="block text-[10px] font-mono text-muted-foreground truncate">
                          {o.hint}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground flex justify-between items-center bg-secondary/20">
            <span data-testid={`${testid}-count`}>{value.length} selected</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm h-7 text-xs"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-done`}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {showChips && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 text-[11px] bg-secondary/60 border border-border rounded-sm px-1.5 py-0.5"
            >
              <span className="truncate max-w-[160px]">{labelFor(v)}</span>
              <button
                type="button"
                onClick={() => toggle(v)}
                className="text-muted-foreground hover:text-rose-700"
                aria-label={`Remove ${labelFor(v)}`}
                data-testid={`${testid}-chip-remove-${v}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
